/**
 * **Le mode découverte, à l'écran** — avenant **A-9**, **réduit à 4 leçons par ⟦A-129 · C11⟧**.
 *
 * *« Le jeu s'arrête, tu as tout qui est un peu noir, un peu fond opaque, et tu as une pop-up
 * qui t'explique les trucs avant que ça démarre. »* (Mathieu, après playtest A-9.) Puis, après
 * le playtest A-129 : *« tu te fais arrêter toutes les 3 secondes […] qui n'a pas trop
 * d'incidence »* — d'où le tri : ce voile ne sert plus qu'aux 4 leçons qui le méritent
 * vraiment (`briefing`, `evenement`, `horloge`, `commentaires` — les seules `bloquante: true`
 * de `game/decouverte.ts`). Toutes les autres sont devenues des info-bulles non bloquantes,
 * rendues par `InfoBulleDecouverte`, à côté.
 *
 * Ce fichier fait deux choses, et c'est volontaire qu'elles soient au même endroit :
 *
 * 1. **Le guetteur** — il regarde l'état de la run et demande la prochaine leçon BLOQUANTE due
 *    (`leconDue`, qui filtre déjà sur `bloquante`). Il se tait dès qu'un autre moment occupe
 *    l'écran (palier, chute, dialogue, Atelier) : une leçon par-dessus une célébration de
 *    palier, ce n'est plus un programme, c'est un embouteillage.
 * 2. **La surface** — le voile opaque et la fiche. Le voile est **bloquant** : il couvre tout,
 *    donc plus un bouton du jeu n'est atteignable tant que la leçon est là. C'est ça, « le jeu
 *    s'arrête » — et c'est aussi ce qui garantit qu'aucune horloge de mini-jeu ne tourne
 *    derrière (l'Atelier, lui, ne monte carrément pas le jeu tant que la leçon est ouverte).
 *
 * Les leçons de mini-jeu ne passent PAS par le guetteur : leur contenu est fusionné dans
 * l'écran d'intro de l'Atelier lui-même (⟦A-129 · C11⟧, `game/decouverte.ts`).
 *
 * ## ⟦C57 · lot 3 · A-174⟧ « À retenir » seul à l'écran, le corps derrière « Les dessous »
 *
 * Le voile pesait 130 mots (le chapeau, deux ou trois paragraphes, « à retenir », le pied) : c'est un
 * arrêt de jeu, donc le dialogue qu'on lit le moins volontiers. Il garde : le titre (5 mots au plus),
 * le chapeau, « À retenir » en UNE phrase de 12 mots au plus (`a_retenir_court`, écrite à côté de
 * `aRetenir`, jamais à sa place) et le pied abrégé. Les paragraphes (`paragraphes()`, l'annonce de
 * l'escalier comprise) et le « À retenir » d'avant s'ouvrent par « Les dessous » : rien n'est supprimé.
 */

import { useEffect, useRef } from "react";

import { aRetenirCourtDe, paragraphes, titreCourtDe } from "../game/decouverte";
import { leconDue, useDecouverteStore } from "../stores/decouverteStore";
import { useRunStore } from "../stores/runStore";
import { useUIStore } from "../stores/uiStore";
import { DessousBloc, LesDessous } from "./LesDessous";
import { Fenetre } from "./Fenetre";
import { useSfx } from "./SoundProvider";

export function ModeDecouverte() {
  const run = useRunStore((s) => s.run);
  const state = run?.state ?? null;
  const runId = run?.runId ?? null;

  const actif = useUIStore((s) => s.settings.decouverte);
  const setSetting = useUIStore((s) => s.setSetting);
  // Un dialogue maison à l'écran (palier, ouverture d'étage, fin de run) passe devant : on
  // attend qu'il soit refermé. `courante` bloque en retour ces composants-là, donc aucun des
  // deux ne peut s'installer par-dessus l'autre.
  const dialogue = useUIStore((s) => s.dialog);
  const momentsSuspendus = useUIStore((s) => s.momentsSuspendus);
  const paliers = useRunStore((s) => s.paliersEnAttente.length);
  const chute = useRunStore((s) => s.chute);

  const courante = useDecouverteStore((s) => s.courante);
  const vues = useDecouverteStore((s) => s.vues);
  const tourDeLaDerniere = useDecouverteStore((s) => s.tourDeLaDerniere);
  const synchroniser = useDecouverteStore((s) => s.synchroniser);
  const demander = useDecouverteStore((s) => s.demander);
  const fermer = useDecouverteStore((s) => s.fermer);

  const bouton = useRef<HTMLButtonElement>(null);
  const sfx = useSfx();

  // La mémoire suit la run : changer de chaîne rejoue tout le programme.
  useEffect(() => {
    if (runId) synchroniser(runId);
  }, [runId, synchroniser]);

  const libre = !courante && !dialogue && !momentsSuspendus && paliers === 0 && chute === null;

  useEffect(() => {
    if (!actif || !state || !libre) return;
    const due = leconDue(state);
    if (due) demander(due.id);
    // `vues` et `tourDeLaDerniere` sont dans les dépendances exprès : fermer une leçon doit
    // réévaluer s'il y en a une autre due (au tour 0, le briefing en enchaîne deux).
  }, [actif, state, libre, vues, tourDeLaDerniere, demander]);

  useEffect(() => {
    if (!courante) return;
    sfx("dialog_open");
    bouton.current?.focus();
  }, [courante, sfx]);

  if (!courante) return null;

  function terminer(couperLeMode: boolean) {
    sfx("dialog_close");
    if (couperLeMode) setSetting("decouverte", false);
    fermer(state?.turn ?? null);
  }

  // ⟦C48 · A-168 §3⟧ La fiche est une `Fenetre` (titre et icône de la leçon dans le bandeau, boutons
  // dans le pied fixe) : c'était une carte crème penchée sur un voile noir. Pas de croix — « Ne plus
  // rien m'expliquer » et « J'ai compris » sont les deux seules sorties, comme avant.
  return (
    <div className="decouverte-voile" role="presentation">
      <div className="decouverte-fenetre">
        <Fenetre
          titre={titreCourtDe(courante)}
          icone={courante.icone}
          role="dialog"
          ariaModal
          className="fenetre-dialogue fenetre-decouverte"
          pied={
            <div className="decouverte-actions">
              <button
                type="button"
                className="bouton discret decouverte-couper"
                onClick={() => terminer(true)}
              >
                Ne plus rien m'expliquer
              </button>
              <button
                type="button"
                ref={bouton}
                className="bouton primaire decouverte-compris"
                onClick={() => terminer(false)}
              >
                J'ai compris
              </button>
            </div>
          }
        >
          <p className="decouverte-chapeau">{courante.chapeau}</p>

          <p className="decouverte-retenir">
            <span className="decouverte-retenir-label">À retenir</span>
            {aRetenirCourtDe(courante)}
          </p>

          {/* ⟦C57⟧ Les paragraphes d'avant (et le titre long, et « À retenir » en entier), tels quels. */}
          <LesDessous titre={courante.titre} className="decouverte-dessous">
            <DessousBloc>
              {paragraphes(courante).map((p, i) => (
                <p className="dessous-texte" key={i}>
                  {p}
                </p>
              ))}
            </DessousBloc>
            {courante.aRetenir !== aRetenirCourtDe(courante) ? (
              <DessousBloc titre="À retenir">
                <p className="dessous-texte">{courante.aRetenir}</p>
              </DessousBloc>
            ) : null}
          </LesDessous>

          <p className="decouverte-pied">Une fois par partie, rallumable dans les Réglages.</p>
        </Fenetre>
      </div>
    </div>
  );
}
